import React, { useCallback, useMemo, useState } from 'react';
import cn from 'classnames';
import Container from 'components/UI/Container';
import Title from 'components/UI/Title';
import { Grid, GridItem } from 'components/UI/Grid';
import Slider from 'components/UI/Slider';
import { CustomDot, CustomPagination } from 'components/UI/Slider/utils';
import Link from 'components/UI/Link';
import LazyImage from 'components/UI/LazyImage';
import { useLang, useTrans, useIsUSA } from 'utils/localisation';
import { useWindowResize } from 'utils/hooks';
import { withErrorBoundary } from 'components/ErrorBoundary';

import * as styles from './Customers.module.scss';
import customersLogos from './customers-logos.json';

const Wrapper = ({ link, className, icon, alt }) =>
  link ? (
    <Link className={className} href={link} target="_blank">
      <LazyImage src={icon} className={styles.icon} alt={alt ?? 'Icon'} />
    </Link>
  ) : (
    <div className={className}>
      <LazyImage src={icon} className={styles.icon} alt={alt ?? 'Icon'} />
    </div>
  );

function Customers({ title, logos: customLogos, rtl = false }) {
  const [current, setCurrent] = useState(0);
  const lang = useLang();
  const t = useTrans(['customers']);
  const isUSA = useIsUSA();
  const { width } = useWindowResize();
  const preparedTitle = title ?? t('title');

  const defaultLogos = useMemo(() => {
    const english = lang === 'en' || lang === 'uk' || lang === 'kz';
    if (english && isUSA) {
      return customersLogos.us;
    }

    return customersLogos[lang] ?? customersLogos.en;
  }, [lang, customersLogos, isUSA]);

  const logos = customLogos ?? defaultLogos;
  const chunkedLogos = logos.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / 4);

    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = [];
    }

    resultArray[chunkIndex].push(item);

    return resultArray;
  }, []);
  const mobileLogos = rtl ? Array.from(chunkedLogos).reverse() : chunkedLogos;

  const setCurrentKey = useCallback((prev, next) => {
    setCurrent(next);
  }, []);

  const gridConfig = {
    xsm: { cols: 2 },
    sm: { cols: 4 },
    md: { cols: 4 },
    lg: { cols: 4 },
    xl: { cols: 4 },
    gutterX: '20px',
    gutterY: '20px',
  };
  const sliderSettings = useMemo(
    () => ({
      customPaging: CustomDot.bind(null, mobileLogos.length, current),
      appendDots: CustomPagination,
      dotsClass: 'slick-pagination',
      dots: true,
      slidesToShow: 1,
      slidesToScroll: 1,
      arrows: false,
      infinite: true,
      rtl,
    }),
    [mobileLogos.length, current],
  );

  return (
    <Container id="clients">
      <div className={cn(styles.layout, 'defaultSlider')}>
        {preparedTitle && <Title className={styles.title}>{preparedTitle}</Title>}
        <div>
          {width > 545 ? (
            <Grid {...gridConfig}>
              {logos.map(({ icon, alt, link }) => (
                <GridItem key={icon}>
                  <Wrapper icon={icon} link={link} className={styles.iconWrapper} alt={alt} />
                </GridItem>
              ))}
            </Grid>
          ) : (
            <div className={styles.slider}>
              <Slider {...sliderSettings} beforeChange={setCurrentKey}>
                {mobileLogos.map((items, i) => (
                  <Grid {...gridConfig} key={`Slide-${i}`}>
                    {items.map(({ icon, alt, link }) => (
                      <GridItem key={icon}>
                        <Wrapper icon={icon} link={link} className={styles.iconWrapper} alt={alt} />
                      </GridItem>
                    ))}
                  </Grid>
                ))}
              </Slider>
            </div>
          )}
        </div>
      </div>
    </Container>
  );
}

Customers.displayName = 'Customers';

export default withErrorBoundary(Customers);
